import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  TextField,
  IconButton,
  Tabs,
  Tab,
  Box,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import MicIcon from "@mui/icons-material/Mic";
import { useSpeechRecognition } from '../../utils/speechRecognition';
import {
  handleSpeechInputChange,
  handleSpeechKeyDown,
  startListening as startListeningHelper,
  stopListening as stopListeningHelper
} from '../../utils/speechRecognitionHelpers';
import classes from "./DataWorkbenchChat.module.scss";
import aggregatorSectionAPIService from "../../services/aggregator_api_service";

import { getFormattedTime } from "../../utils/date";
import { fetchTableColumnData } from 'store';
import { useSelector, useDispatch } from "react-redux";
import { setChatHistories } from "../../store/slices/dataWorkbenchChatSlice";
import { setSqlQuery, setUserInput } from '../../store/slices/aggregatorSlice';

const DataWorkbenchChat = React.memo(
  ({ defaultQuery = "" } = {}) => {

    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [selectedChat, setSelectedChat] = useState(0);
    const messagesEndRef = useRef(null);
    // Speech-to-text states
    const [supported, setSupported] = useState(true);
    const [listening, setListening] = useState(false);
    const finalRef = useRef("");
    const recognitionRef = useRef(null);
    // Speech Recognition setup
    useSpeechRecognition({
      finalRef,
      setInput,
      setListening,
      setSupported,
      recognitionRef,
      lang: 'en-US',
      continuous: true,
      interimResults: true,
    });

    const startListening = () => startListeningHelper(recognitionRef, setListening, input, finalRef);
    const stopListening = () => stopListeningHelper(recognitionRef, setListening);

    const dispatch = useDispatch();

    const reduxChatHistories = useSelector((state) => state.dataWorkbenchChat.chatHistories);
    const initialChat = useMemo(() => [
      {
        role: "assistant",
        content: defaultQuery,
        timestamp: getFormattedTime(),
      },
    ], [defaultQuery]);

    const chatHistories = useMemo(() => (
      reduxChatHistories && reduxChatHistories.length > 0 ? reduxChatHistories : [initialChat]
    ), [reduxChatHistories, initialChat]);

    useEffect(() => {
      if (!chatHistories) return;
      const selectedItem = chatHistories[selectedChat]
      const lastElement = selectedItem && selectedItem[selectedItem.length - 1]
      if (messagesEndRef.current && lastElement?.role === 'user') {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
      // eslint-disable-next-line
    }, [chatHistories, selectedChat]);

    const handleSendMessage = async () => {
      if (!input.trim() || !chatHistories) return;
      stopListening();

      const userMessage = {
        role: "user",
        content: input,
        timestamp: getFormattedTime(),
      };
      dispatch(setUserInput(input));

      const updatedChats = [...chatHistories];
      updatedChats[selectedChat] = [
        ...updatedChats[selectedChat],
        userMessage,
      ];
      dispatch(setChatHistories(updatedChats));
      setIsLoading(true);

      const conversationHistory = [
        ...chatHistories[selectedChat].map((chat) => ({
          role: chat.role === "user" ? "user" : "assistant",
          content: [{ text: chat.content }],
        })),
        { role: "user", content: [{ text: input }] },
      ];

      const apiResponse = await fetchChatResponse(conversationHistory);

      if (apiResponse) {
        await dispatch(fetchTableColumnData());
      }

      dispatch(setSqlQuery(apiResponse.sql_query));

      const conversation = apiResponse.conversation
      const lastContent = conversation[conversation.length - 1].content;

      const updatedChatsAfter = [...updatedChats];
      updatedChatsAfter[selectedChat] = [
        ...updatedChatsAfter[selectedChat],
        { role: "assistant", content: lastContent[0].text, timestamp: getFormattedTime() },
      ];
      dispatch(setChatHistories(updatedChatsAfter));
      setIsLoading(false);
      setInput("");
      finalRef.current = '';
    };

    const fetchChatResponse = async (conversationHistory) => {
      const payload = {
        conversation: conversationHistory,
      }
      const chatResponse = await aggregatorSectionAPIService.fetchChatResponse(payload);
      return chatResponse
    };

    const handleKeyDown = (event) => handleSpeechKeyDown(event, stopListening, handleSendMessage);
    const handleInputChange = handleSpeechInputChange(setInput, finalRef);

    const handleTabChange = useCallback((event, newValue) => {
      setSelectedChat(newValue);
    }, []);

    const handleAddChat = useCallback(() => {
      if (!chatHistories) return;
      const updatedChats = [...chatHistories, []];
      dispatch(setChatHistories(updatedChats));
      setSelectedChat(chatHistories.length);
    }, [chatHistories, dispatch]);

    const tabs = useMemo(
      () => (
        <Tabs
          value={selectedChat}
          onChange={handleTabChange}
          aria-label="chat tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          className={classes.tabs}
        >
          {chatHistories && chatHistories.map((chat, index) => (
            <Tab key={index} label={`Chat ${index + 1}`} />
          ))}
          <IconButton
            className={classes.addChatButton}
            aria-label="add chat"
            onClick={handleAddChat}
          >
            <AddIcon />
          </IconButton>
        </Tabs>
      ),
      [selectedChat, chatHistories, handleAddChat, handleTabChange]
    );

    return (
      <div className={classes.chatContainer}>
        <div className={classes.headline}>
          <h3 className={classes.title}>Data Workbench</h3>
        </div>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>{tabs}</Box>
        <div className={classes.chatBox}>
          {chatHistories && chatHistories[selectedChat]?.map((chat, index) => (
            <div
              key={index}
              className={
                chat.role === "user" ? classes.userMessage : classes.apiMessage
              }
            >
              <div
                className={classes.chatBubble}
                dangerouslySetInnerHTML={{
                  __html: chat?.content?.replaceAll("\n", "<br />"),
                }}
              ></div>{" "}
              <div className={classes.timestamp}>{chat.timestamp}</div>
            </div>
          ))}

          {isLoading && (
            <div className={classes.typingIndicator}>
              <CircularProgress size={20} />
            </div>
          )}
          <div ref={messagesEndRef} />

        </div>

        <div className={classes.inputContainer}>
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={3}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            className={classes.textInput}
            autoComplete="off"
            placeholder="Type your message..."
            disabled={isLoading}
            inputRef={el => {
              if (el && listening === true) {
                el.selectionStart = el.value.length;
                el.selectionEnd = el.value.length;
                el.focus();
                // Move caret to the end (for textarea)
                const len = el.value.length;
                el.setSelectionRange(len, len);
                el.scrollLeft = el.scrollWidth;
              }
            }}
          />
          {/* Speech to Text Buttons */}
          {supported ? (
            <>
              <IconButton
                onClick={listening ? stopListening : startListening}
                color={listening ? "secondary" : "primary"}
                aria-label="speech to text"
                className={`${classes.sendButton} ${listening ? classes.micListening : ""}`}
                disabled={isLoading}
              >
                <MicIcon
                  style={
                    listening
                      ? {
                        animation: "pulse 1s infinite",
                        color: "#d32f2f",
                        filter: "drop-shadow(0 0 6px #d32f2f)",
                      }
                      : {}
                  }
                />
              </IconButton>
            </>
          ) : (
            <span style={{ color: "red", marginRight: 8, fontSize: 12 }}>
              Speech API not supported
            </span>
          )}
          <IconButton
            onClick={handleSendMessage}
            color="primary"
            aria-label="send message"
            className={classes.sendButton}
            disabled={isLoading}
          >
            <ArrowUpwardIcon />
          </IconButton>
          {/* Add keyframes for pulse animation */}
          <style>
            {`
              @keyframes pulse {
                0% { transform: scale(1); }
                50% { transform: scale(1.2); }
                100% { transform: scale(1); }
              }
            `}
          </style>
        </div>
      </div>
    );
  }
);

export default DataWorkbenchChat; 